import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { ColumnsPanelTrigger, Toolbar, ToolbarButton } from '@mui/x-data-grid';
import { Track } from 'models/Track';
import { TrackRequest } from 'models/TrackRequest';
import { useTranslation } from 'react-i18next';
import { formatDurationHuman } from 'utils/formatDurationHuman';
import { RequestListToolbarProps } from './RequestListToolbar';

export type RequestListDeletedToolbarProps = Omit<
  RequestListToolbarProps,
  'playlistRequests' | 'onDelete' | 'onExport' | 'onReload' | 'onAddRequestsDialog'
> & {
  requests: TrackRequest[];
  selectedRequests: TrackRequest[];
  onRestore(requests: TrackRequest[]): void;
};

export function RequestListDeletedToolbar({
  requests,
  selectedRequests,
  onRestore,
}: RequestListDeletedToolbarProps) {
  const { t } = useTranslation();
  return (
    <Toolbar>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flex: 1 }}>
        <Tooltip title="Columns" placement="top">
          <ColumnsPanelTrigger render={<ToolbarButton />}>
            <ViewColumnIcon fontSize="small" />
          </ColumnsPanelTrigger>
        </Tooltip>
        <Box sx={{ flex: 1 }}>
          {selectedRequests.length > 0 && (
            <ToolbarButton
              render={
                <Button
                  variant="outlined"
                  color="success"
                  sx={{ lineHeight: 'normal' }}
                  onClick={() => onRestore?.(selectedRequests)}
                />
              }
            >
              {t('restoreLength', 'Restore ({{length}})', { length: selectedRequests.length })}
            </ToolbarButton>
          )}
        </Box>
        <Box sx={{ px: 1 }}>
          {selectedRequests.length > 0 ? (
            <Typography>
              {t('lengthSelected', '{{length}} selected', { length: selectedRequests.length })}
              {', '}
              {formatDurationHuman(
                selectedRequests.reduce((sum, request) => {
                  const track = request.value as Track | undefined;
                  return sum + (track?.duration ?? 0);
                }, 0)
              )}
            </Typography>
          ) : (
            <Typography>
              {t('lengthDeletedRequest', '{{length}} deleted request', { length: requests.length })}
              {', '}
              {formatDurationHuman(
                requests.reduce((sum, request) => {
                  const track = request.value as Track | undefined;
                  return sum + (track?.duration ?? 0);
                }, 0)
              )}
            </Typography>
          )}
        </Box>
      </Stack>
    </Toolbar>
  );
}
